import React, { useState, useEffect, useCallback } from 'react';
import { FaPlus, FaEdit, FaTrash, FaFileDownload, FaUsers, FaFilePdf } from 'react-icons/fa';
import Swal from 'sweetalert2';
import declaracionesJuradasService from '../../services/declaracionesJuradasService';
import '../../styles/components/DJ1887RemuneracionesTab.css';

/**
 * Componente para gestionar DJ1887 - Rentas del Art. 42 N°1 (Sueldos)
 *
 * Permite crear, editar, eliminar registros y generar CSV con formato SII
 * Diseñado para MCP (Model Context Protocol)
 *
 * @param {Object} props
 * @param {number} props.declaracionId - ID de la declaración jurada
 * @param {number} props.year - Año de la declaración
 * @param {number} props.idCompany - ID de la empresa
 * @param {string} props.rutEmpresa - RUT de la empresa
 * @param {string} props.token - Token de autenticación
 */
const DJ1887RemuneracionesTab = ({
    declaracionId,
    year,
    idCompany,
    rutEmpresa,
    token
}) => {
    const [registros, setRegistros] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingRegistro, setEditingRegistro] = useState(null);

    const [formData, setFormData] = useState({
        rut_trabajador: '',
        nombre_trabajador: '',
        renta_total_neta_pagada: '',
        impuesto_unico_retenido: '',
        mayor_retencion_solicitada: '',
        renta_total_no_gravada: '',
        renta_total_exenta: '',
        rebaja_zonas_extremas: '',
        prestamos_tasa_0: '',
        periodo_rentas: 'C',
        renta_enero: '',
        renta_febrero: '',
        renta_marzo: '',
        renta_abril: '',
        renta_mayo: '',
        renta_junio: '',
        renta_julio: '',
        renta_agosto: '',
        renta_septiembre: '',
        renta_octubre: '',
        renta_noviembre: '',
        renta_diciembre: '',
        numero_certificado: '',
        cert_enero: '',
        cert_febrero: '',
        cert_marzo: '',
        cert_abril: '',
        cert_mayo: '',
        cert_junio: '',
        cert_julio: '',
        cert_agosto: '',
        cert_septiembre: '',
        cert_octubre: '',
        cert_noviembre: '',
        cert_diciembre: '',
        horas_semanales_pactadas: ''
    });

    const loadRegistros = useCallback(async () => {
        try {
            setLoading(true);
            const data = await declaracionesJuradasService.getRemuneraciones(
                declaracionId,
                idCompany,
                token
            );
            setRegistros(data);
        } catch (error) {
            console.error('Error al cargar registros:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error al cargar los registros'
            });
        } finally {
            setLoading(false);
        }
    }, [declaracionId, idCompany, token]);

    // Cargar registros al montar el componente
    useEffect(() => {
        loadRegistros();
    }, [loadRegistros]);

    const handleOpenModal = (registro = null) => {
        if (registro) {
            setEditingRegistro(registro);
            setFormData({
                rut_trabajador: registro.rut_trabajador || '',
                nombre_trabajador: registro.nombre_trabajador || '',
                renta_total_neta_pagada: registro.renta_total_neta_pagada || '',
                impuesto_unico_retenido: registro.impuesto_unico_retenido || '',
                mayor_retencion_solicitada: registro.mayor_retencion_solicitada || '',
                renta_total_no_gravada: registro.renta_total_no_gravada || '',
                renta_total_exenta: registro.renta_total_exenta || '',
                rebaja_zonas_extremas: registro.rebaja_zonas_extremas || '',
                prestamos_tasa_0: registro.prestamos_tasa_0 || '',
                periodo_rentas: registro.periodo_rentas || 'C',
                renta_enero: registro.renta_enero || '',
                renta_febrero: registro.renta_febrero || '',
                renta_marzo: registro.renta_marzo || '',
                renta_abril: registro.renta_abril || '',
                renta_mayo: registro.renta_mayo || '',
                renta_junio: registro.renta_junio || '',
                renta_julio: registro.renta_julio || '',
                renta_agosto: registro.renta_agosto || '',
                renta_septiembre: registro.renta_septiembre || '',
                renta_octubre: registro.renta_octubre || '',
                renta_noviembre: registro.renta_noviembre || '',
                renta_diciembre: registro.renta_diciembre || '',
                numero_certificado: registro.numero_certificado || '',
                cert_enero: registro.cert_enero || '',
                cert_febrero: registro.cert_febrero || '',
                cert_marzo: registro.cert_marzo || '',
                cert_abril: registro.cert_abril || '',
                cert_mayo: registro.cert_mayo || '',
                cert_junio: registro.cert_junio || '',
                cert_julio: registro.cert_julio || '',
                cert_agosto: registro.cert_agosto || '',
                cert_septiembre: registro.cert_septiembre || '',
                cert_octubre: registro.cert_octubre || '',
                cert_noviembre: registro.cert_noviembre || '',
                cert_diciembre: registro.cert_diciembre || '',
                horas_semanales_pactadas: registro.horas_semanales_pactadas || ''
            });
        } else {
            setEditingRegistro(null);
            setFormData({
                rut_trabajador: '',
                nombre_trabajador: '',
                renta_total_neta_pagada: '',
                impuesto_unico_retenido: '',
                mayor_retencion_solicitada: '',
                renta_total_no_gravada: '',
                renta_total_exenta: '',
                rebaja_zonas_extremas: '',
                prestamos_tasa_0: '',
                periodo_rentas: 'C',
                renta_enero: '',
                renta_febrero: '',
                renta_marzo: '',
                renta_abril: '',
                renta_mayo: '',
                renta_junio: '',
                renta_julio: '',
                renta_agosto: '',
                renta_septiembre: '',
                renta_octubre: '',
                renta_noviembre: '',
                renta_diciembre: '',
                numero_certificado: '',
                cert_enero: '',
                cert_febrero: '',
                cert_marzo: '',
                cert_abril: '',
                cert_mayo: '',
                cert_junio: '',
                cert_julio: '',
                cert_agosto: '',
                cert_septiembre: '',
                cert_octubre: '',
                cert_noviembre: '',
                cert_diciembre: '',
                horas_semanales_pactadas: ''
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingRegistro(null);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validaciones básicas
        if (!formData.rut_trabajador || !formData.nombre_trabajador ||
            !formData.renta_total_neta_pagada) {
            Swal.fire({
                icon: 'warning',
                title: 'Campos requeridos',
                text: 'Debe completar todos los campos obligatorios'
            });
            return;
        }

        try {
            if (editingRegistro) {
                await declaracionesJuradasService.updateRemuneracion(
                    editingRegistro.id,
                    formData,
                    idCompany,
                    token
                );
                Swal.fire({
                    icon: 'success',
                    title: 'Actualizado',
                    text: 'Registro actualizado exitosamente'
                });
            } else {
                await declaracionesJuradasService.createRemuneracion(
                    declaracionId,
                    formData,
                    idCompany,
                    token
                );
                Swal.fire({
                    icon: 'success',
                    title: 'Creado',
                    text: 'Registro creado exitosamente'
                });
            }
            handleCloseModal();
            loadRegistros();
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'Error al guardar registro'
            });
        }
    };

    const handleDelete = async (registro) => {
        const result = await Swal.fire({
            icon: 'warning',
            title: '¿Está seguro?',
            text: 'Esta acción no se puede deshacer',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await declaracionesJuradasService.deleteRemuneracion(
                    registro.id,
                    idCompany,
                    token
                );
                Swal.fire({
                    icon: 'success',
                    title: 'Eliminado',
                    text: 'Registro eliminado exitosamente'
                });
                loadRegistros();
            } catch (error) {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: error.message || 'Error al eliminar registro'
                });
            }
        }
    };

    const handleDownloadCSV = async () => {
        if (registros.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Sin datos',
                text: 'No hay registros para generar el CSV'
            });
            return;
        }

        try {
            // Primero validar
            const validation = await declaracionesJuradasService.validateDJ1887Data(
                declaracionId,
                idCompany,
                token
            );

            if (!validation.valid) {
                const result = await Swal.fire({
                    icon: 'warning',
                    title: 'Errores de validación',
                    html: `Se encontraron errores:<br/><ul style="text-align: left;">${validation.errors.map(e => `<li>${e}</li>`).join('')}</ul><br/>¿Desea descargar el CSV de todas formas?`,
                    showCancelButton: true,
                    confirmButtonText: 'Sí, descargar',
                    cancelButtonText: 'Cancelar'
                });

                if (!result.isConfirmed) {
                    return;
                }
            }

            // Descargar CSV
            await declaracionesJuradasService.downloadAndSaveDJ1887CSV(
                declaracionId,
                idCompany,
                rutEmpresa,
                year,
                token
            );

            Swal.fire({
                icon: 'success',
                title: 'Descarga exitosa',
                text: 'El archivo CSV se ha descargado correctamente'
            });
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'Error al descargar CSV'
            });
        }
    };

    // Descargar PDF
    const handleDownloadPDF = async () => {
        if (registros.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Sin datos',
                text: 'No hay registros para generar el PDF'
            });
            return;
        }

        try {
            await declaracionesJuradasService.downloadAndSaveDJ1887PDF(
                declaracionId,
                idCompany,
                rutEmpresa,
                year,
                token
            );

            Swal.fire({
                icon: 'success',
                title: 'Descarga exitosa',
                text: 'El archivo PDF se ha descargado correctamente'
            });
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'Error al descargar PDF'
            });
        }
    };

    return (
        <div className="dj1887-tab">
            {/* Header */}
            <div className="tab-header">
                <div className="tab-title">
                    <FaUsers className="title-icon" />
                    <div>
                        <h2>DJ 1887 - Rentas del Art. 42 N°1 (Sueldos)</h2>
                        <p className="subtitle">Año {year} | Total: {registros.length} trabajadores</p>
                    </div>
                </div>
                <div className="header-actions">
                    <button
                        className="btn-download"
                        onClick={handleDownloadCSV}
                        disabled={loading || registros.length === 0}
                    >
                        <FaFileDownload /> Descargar CSV
                    </button>
                    <button
                        className="btn-pdf"
                        onClick={handleDownloadPDF}
                        disabled={loading || registros.length === 0}
                        title="Descargar PDF con formato oficial"
                    >
                        <FaFilePdf /> Descargar PDF
                    </button>
                    <button
                        className="btn-nuevo"
                        onClick={() => handleOpenModal()}
                        disabled={loading}
                    >
                        <FaPlus /> Nuevo Registro
                    </button>
                </div>
            </div>

            {/* Tabla de registros */}
            <div className="table-container">
                {loading ? (
                    <div className="loading-message">Cargando...</div>
                ) : registros.length === 0 ? (
                    <div className="empty-message">
                        <FaUsers className="empty-icon" />
                        <p>No hay remuneraciones registradas</p>
                        <button className="btn-primary" onClick={() => handleOpenModal()}>
                            <FaPlus /> Agregar Primer Registro
                        </button>
                    </div>
                ) : (
                    <table className="registros-table">
                        <thead>
                            <tr>
                                <th>N°</th>
                                <th>RUT Trabajador</th>
                                <th>Nombre</th>
                                <th>Renta Total Neta</th>
                                <th>Impuesto Retenido</th>
                                <th>Horas Semanales</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {registros.map((registro) => (
                                <tr key={registro.id}>
                                    <td>{registro.numero}</td>
                                    <td>{registro.rut_trabajador}</td>
                                    <td>{registro.nombre_trabajador}</td>
                                    <td>${parseInt(registro.renta_total_neta_pagada || 0).toLocaleString('es-CL')}</td>
                                    <td>${parseInt(registro.impuesto_unico_retenido || 0).toLocaleString('es-CL')}</td>
                                    <td>{registro.horas_semanales_pactadas || '-'}</td>
                                    <td className="actions-cell">
                                        <button
                                            className="btn-icon btn-edit"
                                            onClick={() => handleOpenModal(registro)}
                                            title="Editar"
                                        >
                                            <FaEdit />
                                        </button>
                                        <button
                                            className="btn-icon btn-delete"
                                            onClick={() => handleDelete(registro)}
                                            title="Eliminar"
                                        >
                                            <FaTrash />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modal de formulario */}
            {showModal && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{editingRegistro ? 'Editar' : 'Nueva'} Remuneración</h3>
                            <button className="btn-close" onClick={handleCloseModal}>×</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="form-sections">
                                {/* Datos del Trabajador */}
                                <div className="form-section">
                                    <h4>Datos del Trabajador</h4>
                                    <div className="form-grid">
                                        <div className="form-group">
                                            <label htmlFor="rut_trabajador">RUT Trabajador *</label>
                                            <input
                                                type="text"
                                                id="rut_trabajador"
                                                name="rut_trabajador"
                                                value={formData.rut_trabajador}
                                                onChange={handleInputChange}
                                                required
                                                placeholder="Ej: 12345678-9"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="nombre_trabajador">Nombre Trabajador *</label>
                                            <input
                                                type="text"
                                                id="nombre_trabajador"
                                                name="nombre_trabajador"
                                                value={formData.nombre_trabajador}
                                                onChange={handleInputChange}
                                                required
                                                placeholder="Nombre completo"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="periodo_rentas">Periodo de Rentas *</label>
                                            <select
                                                id="periodo_rentas"
                                                name="periodo_rentas"
                                                value={formData.periodo_rentas}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="C">C - Trabajador no agrícola jornada completa</option>
                                                <option value="P">P - Trabajador no agrícola jornada parcial</option>
                                                <option value="A">A - Trabajador agrícola jornada completa</option>
                                                <option value="B">B - Trabajador agrícola jornada parcial</option>
                                                <option value="G">G - Autoridad de gobierno (Art. 52 bis)</option>
                                                <option value="S">S - Senadores y diputados</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="horas_semanales_pactadas">Horas Semanales Pactadas</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                id="horas_semanales_pactadas"
                                                name="horas_semanales_pactadas"
                                                value={formData.horas_semanales_pactadas}
                                                onChange={handleInputChange}
                                                placeholder="Ej: 45.00 (99 si no especifica)"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Montos Anuales */}
                                <div className="form-section">
                                    <h4>Montos Anuales</h4>
                                    <div className="form-grid">
                                        <div className="form-group">
                                            <label htmlFor="renta_total_neta_pagada">Renta Total Neta Pagada *</label>
                                            <input
                                                type="number"
                                                id="renta_total_neta_pagada"
                                                name="renta_total_neta_pagada"
                                                value={formData.renta_total_neta_pagada}
                                                onChange={handleInputChange}
                                                required
                                                placeholder="Ej: 12000000"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="impuesto_unico_retenido">Impuesto Único Retenido</label>
                                            <input
                                                type="number"
                                                id="impuesto_unico_retenido"
                                                name="impuesto_unico_retenido"
                                                value={formData.impuesto_unico_retenido}
                                                onChange={handleInputChange}
                                                placeholder="Ej: 1200000"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="mayor_retencion_solicitada">Mayor Retención Solicitada (Art. 88)</label>
                                            <input
                                                type="number"
                                                id="mayor_retencion_solicitada"
                                                name="mayor_retencion_solicitada"
                                                value={formData.mayor_retencion_solicitada}
                                                onChange={handleInputChange}
                                                placeholder="Ej: 0"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="renta_total_no_gravada">Renta Total No Gravada</label>
                                            <input
                                                type="number"
                                                id="renta_total_no_gravada"
                                                name="renta_total_no_gravada"
                                                value={formData.renta_total_no_gravada}
                                                onChange={handleInputChange}
                                                placeholder="Ej: 0"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="renta_total_exenta">Renta Total Exenta</label>
                                            <input
                                                type="number"
                                                id="renta_total_exenta"
                                                name="renta_total_exenta"
                                                value={formData.renta_total_exenta}
                                                onChange={handleInputChange}
                                                placeholder="Ej: 0"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="rebaja_zonas_extremas">Rebaja Zonas Extremas (DL 889)</label>
                                            <input
                                                type="number"
                                                id="rebaja_zonas_extremas"
                                                name="rebaja_zonas_extremas"
                                                value={formData.rebaja_zonas_extremas}
                                                onChange={handleInputChange}
                                                placeholder="Ej: 0"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="prestamos_tasa_0">3% Préstamos Tasa 0% (2020-2021)</label>
                                            <input
                                                type="number"
                                                id="prestamos_tasa_0"
                                                name="prestamos_tasa_0"
                                                value={formData.prestamos_tasa_0}
                                                onChange={handleInputChange}
                                                placeholder="Ej: 0"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Rentas Mensuales */}
                                <div className="form-section">
                                    <h4>Rentas Mensuales</h4>
                                    <div className="form-grid-meses">
                                        {['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
                                          'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'].map(mes => (
                                            <div className="form-group" key={`renta_${mes}`}>
                                                <label htmlFor={`renta_${mes}`}>{mes.charAt(0).toUpperCase() + mes.slice(1)}</label>
                                                <input
                                                    type="number"
                                                    id={`renta_${mes}`}
                                                    name={`renta_${mes}`}
                                                    value={formData[`renta_${mes}`]}
                                                    onChange={handleInputChange}
                                                    placeholder="0"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Certificados */}
                                <div className="form-section">
                                    <h4>Número de Certificados Mensuales</h4>
                                    <div className="form-group">
                                        <label htmlFor="numero_certificado">Número Certificado General</label>
                                        <input
                                            type="text"
                                            id="numero_certificado"
                                            name="numero_certificado"
                                            value={formData.numero_certificado}
                                            onChange={handleInputChange}
                                            placeholder="Número de certificado"
                                        />
                                    </div>
                                    <div className="form-grid-meses">
                                        {['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
                                          'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'].map(mes => (
                                            <div className="form-group" key={`cert_${mes}`}>
                                                <label htmlFor={`cert_${mes}`}>{mes.charAt(0).toUpperCase() + mes.slice(1)}</label>
                                                <input
                                                    type="text"
                                                    id={`cert_${mes}`}
                                                    name={`cert_${mes}`}
                                                    value={formData[`cert_${mes}`]}
                                                    onChange={handleInputChange}
                                                    placeholder="N° Cert"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-primary">
                                    {editingRegistro ? 'Actualizar' : 'Crear'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

        </div>
    );
};

export default DJ1887RemuneracionesTab;